<template>
  <div class="paid-listing-block">
    <AppLoader v-if="isLoading"/>

    <template v-else>
      <section v-if="popularGroups.length" class="paid-popular" aria-labelledby="paid-popular-title">
        <h2 id="paid-popular-title" class="paid-popular__title">{{ popularTitle }}</h2>
        <div
          class="paid-popular__grid"
          :style="popularGridStyle"
        >
          <button
            v-for="group in popularGroups"
            :key="'pop-' + group.groupName"
            type="button"
            class="paid-popular__card"
            @click="openPopularGroup(group.groupName)"
          >
            <span
              class="paid-popular__icon"
              :class="{ 'paid-popular__icon--uploaded': isUploadedSrc(popularIconSrc(group)) }"
              aria-hidden="true"
            >
              <img
                class="paid-popular__icon-img"
                :class="{ 'paid-popular__icon-img--uploaded': isUploadedSrc(popularIconSrc(group)) }"
                :src="popularIconSrc(group)"
                alt=""
              />
            </span>
            <span class="paid-popular__name">{{ popularLabel(group) }}</span>
          </button>
        </div>
      </section>

      <section class="paid-all" aria-labelledby="paid-all-title">
        <div class="paid-all__toolbar">
          <h2 id="paid-all-title" class="paid-all__title">Все услуги</h2>
          <button
            type="button"
            class="paid-all__toggle"
            @click="toggleAllAccordions"
          >
            {{ allExpanded ? 'Свернуть все' : 'Развернуть все' }}
            <span class="paid-all__toggle-icon" :class="{ 'is-open': allExpanded }" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
              </svg>
            </span>
          </button>
        </div>

        <div class="paid-accordion">
          <div
            v-for="itemDepartment in paidList"
            :key="itemDepartment.groupName"
            class="paid-accordion__item"
            :class="{ 'paid-accordion__item--highlight': highlightedGroupAnchor === groupAnchorId(itemDepartment.groupName) }"
            :id="groupAnchorId(itemDepartment.groupName)"
          >
            <button
              type="button"
              class="paid-accordion__btn"
              :aria-expanded="isAccordionOpen(itemDepartment.groupName) ? 'true' : 'false'"
              @click="toggleAccordion(itemDepartment.groupName)"
            >
              <span class="paid-accordion__btn-left">
              <span
                class="paid-accordion__icon"
                :class="{ 'paid-accordion__icon--uploaded': hasCmsGroupIcon(itemDepartment.groupName) }"
                aria-hidden="true"
              >
                  <img
                    class="paid-accordion__icon-img"
                    :class="{ 'paid-accordion__icon-img--uploaded': hasCmsGroupIcon(itemDepartment.groupName) }"
                    :src="groupIconSrc(itemDepartment.groupName)"
                    alt=""
                  />
                </span>
                <span class="paid-accordion__name">{{ itemDepartment.groupName }}</span>
              </span>
              <span
                class="paid-accordion__chevron"
                :class="{ 'is-open': isAccordionOpen(itemDepartment.groupName) }"
                aria-hidden="true"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="currentColor" d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
                </svg>
              </span>
            </button>

            <div v-if="isAccordionOpen(itemDepartment.groupName)" class="paid-accordion__panel">
              <ul class="paid-service-list">
                <template v-for="(service, index) in itemDepartment.services">
                  <AppPaidServicePackageRow
                    v-if="hasPaidPackage(service)"
                    :key="service.id != null ? `pkg-${service.id}` : `pkg-${index}`"
                    :service="service"
                    :paid-groups="paidList"
                    :open="isPackageOpen(service.id)"
                    :highlighted="isHighlightedService(service.id)"
                    :row-id="paidServiceRowId(service.id)"
                    :format-price="formatPrice"
                    @toggle="togglePackage(service.id)"
                  />
                  <li
                    v-else
                    :key="service.id != null ? `svc-${service.id}` : index"
                    class="paid-service-row"
                    :id="paidServiceRowId(service.id)"
                    :class="{ 'paid-service-row--highlight': isHighlightedService(service.id) }"
                  >
                    <span class="paid-service-row__name">{{ service.name }}</span>
                    <span class="paid-service-row__price">{{ formatPrice(service.price) }}</span>
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </div>

        <div class="paid-docs">
          <button
            type="button"
            class="paid-accordion__btn paid-docs__btn"
            :aria-expanded="isOpenInfo ? 'true' : 'false'"
            @click="toggleAccordionInfo"
          >
            <span class="paid-accordion__btn-left">
              <span class="paid-accordion__icon" aria-hidden="true" v-html="icons.doc"></span>
              <span class="paid-accordion__name">Информация о платных услугах</span>
            </span>
            <span
              class="paid-accordion__chevron"
              :class="{ 'is-open': isOpenInfo }"
              aria-hidden="true"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
                <path fill="currentColor" d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z"/>
              </svg>
            </span>
          </button>
          <div v-if="isOpenInfo" class="paid-accordion__panel paid-docs__panel">
            <p class="paid-docs__text">
              Всю дополнительную информацию можете найти в разделе документы по ссылке ниже:
            </p>
            <button type="button" class="paid-docs__link" @click="routePaidServices">
              Документы — платные услуги
            </button>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { actionTypesPaid } from '@/store/modules/paidServices';
import { mapState } from 'vuex';
import AppLoader from '@/components/Loader.vue';
import AppPaidServicePackageRow from '@/components/Blocks/AppPaidServicePackageRow.vue';
import { getPaidPackage } from '@/utils/paidPackages';

const POPULAR_PRIORITY = [
  'Ведение беременности',
  'Женское здоровье и гинекология',
  'Анестезия и интенсивная терапия',
  'Генетика и пренатальная диагностика',
  'Немедицинские услуги',
];

const POPULAR_LIMIT = 5;

const DOC_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none"><path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-6z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M14 2v6h6M9 13h6M9 17h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

const GROUP_ICONS = {
  pregnancy: require('@/assets/img/paid-services/icon-pregnancy.png'),
  hospital: require('@/assets/img/paid-services/icon-hospital.png'),
  family: require('@/assets/ico/family.png'),
  repro: require('@/assets/ico/repro.png'),
  gynecology: require('@/assets/img/paid-services/icon-gynecology.png'),
  consult: require('@/assets/ico/consult.png'),
  scan: require('@/assets/ico/scan.png'),
  lab: require('@/assets/ico/lab.png'),
  pathology: require('@/assets/ico/pathology.png'),
  anesthesia: require('@/assets/img/paid-services/icon-anesthesia.png'),
  support: require('@/assets/ico/support.png'),
  comfort: require('@/assets/img/paid-services/icon-nonmedical.png'),
  education: require('@/assets/ico/education.png'),
  postpartum: require('@/assets/ico/postpartum.png'),
  genetics: require('@/assets/img/paid-services/icon-genetics.png'),
  childbirth: require('@/assets/ico/childbirth.png'),
  default: require('@/assets/ico/consult.png'),
};

export default {
  name: 'AppPaidServicesListingBlock',
  components: { AppLoader, AppPaidServicePackageRow },
  inject: {
    searchState: { default: null },
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      openedDepartments: {},
      openedPackages: {},
      isOpenInfo: false,
      highlightedServiceId: null,
      highlightedGroupAnchor: '',
      highlightClearTimer: null,
      icons: { doc: DOC_ICON },
    };
  },
  computed: {
    ...mapState({
      paid: (state) => state.paidServices.paid,
      isLoading: (state) => state.paidServices.isLoading,
    }),
    paidList() {
      return this.paid || [];
    },
    popularTitle() {
      const t = this.data && this.data.popularTitle;
      return (t != null && String(t).trim()) ? String(t).trim() : 'Популярные услуги';
    },
    configuredPopularItems() {
      const items = this.data && this.data.popularItems;
      return Array.isArray(items) ? items.filter((i) => i && (
        String(i.groupName || '').trim()
        || String(i.label || '').trim()
        || String(i.icon || '').trim()
      )) : [];
    },
    groupIconOverrides() {
      const map = {};
      const rows = this.data && this.data.groupIcons;
      if (Array.isArray(rows)) {
        rows.forEach((row) => {
          const name = row && row.groupName != null ? String(row.groupName).trim() : '';
          const icon = row && row.icon != null ? String(row.icon).trim() : '';
          if (name && icon) map[name] = icon;
        });
      }
      return map;
    },
    popularIconOverrides() {
      const map = {};
      const list = this.paidList;
      const byName = {};
      list.forEach((g) => {
        if (g && g.groupName) byName[g.groupName] = true;
      });
      this.configuredPopularItems.forEach((item) => {
        const resolved = this.resolvePopularGroupName(item, byName);
        const icon = item.icon != null ? String(item.icon).trim() : '';
        if (resolved && icon) map[resolved] = icon;
      });
      return map;
    },
    popularGroups() {
      const list = this.paidList;
      if (!list.length) {
        return [];
      }
      const byName = {};
      list.forEach((g) => {
        if (g && g.groupName) {
          byName[g.groupName] = g;
        }
      });

      const configured = this.configuredPopularItems;
      if (configured.length) {
        const picked = [];
        configured.forEach((item) => {
          const name = this.resolvePopularGroupName(item, byName);
          if (name && byName[name] && !picked.some((p) => p.groupName === name)) {
            picked.push(byName[name]);
          }
        });
        return picked;
      }

      const picked = [];
      POPULAR_PRIORITY.forEach((name) => {
        if (byName[name] && picked.length < POPULAR_LIMIT) {
          picked.push(byName[name]);
        }
      });
      if (picked.length < POPULAR_LIMIT) {
        list.forEach((g) => {
          if (picked.length >= POPULAR_LIMIT) {
            return;
          }
          if (!picked.some((p) => p.groupName === g.groupName)) {
            picked.push(g);
          }
        });
      }
      return picked;
    },
    popularGridStyle() {
      const n = this.popularGroups.length;
      const cols = Math.max(1, Math.min(n || 5, 6));
      return { gridTemplateColumns: `repeat(${cols}, 1fr)` };
    },
    allExpanded() {
      const groups = this.paidList;
      if (!groups.length) {
        return false;
      }
      return groups.every((g) => this.isAccordionOpen(g.groupName));
    },
  },
  watch: {
    '$route.query': {
      deep: true,
      handler() {
        this.$nextTick(() => this.applyServiceFromRoute());
      },
    },
    paid() {
      if (this.normalizeQueryParam(this.$route.query.serviceId) && this.paid && this.paid.length) {
        this.$nextTick(() => this.applyServiceFromRoute());
      }
    },
  },
  mounted() {
    this.$store.dispatch(actionTypesPaid.getPaid)
      .then(() => {
        this.$nextTick(() => this.applyServiceFromRoute());
      })
      .catch(() => {});
  },
  beforeDestroy() {
    if (this.highlightClearTimer) {
      clearTimeout(this.highlightClearTimer);
      this.highlightClearTimer = null;
    }
  },
  methods: {
    resolvePopularGroupName(item, byName) {
      const names = byName && typeof byName === 'object'
        ? Object.keys(byName)
        : [];
      const direct = item && item.groupName != null ? String(item.groupName).trim() : '';
      if (direct && (byName[direct] || names.includes(direct))) return direct;
      const label = item && item.label != null ? String(item.label).trim() : '';
      if (!label) return direct;
      if (byName[label] || names.includes(label)) return label;
      const prefix = names.find((g) => label.startsWith(g) || g.startsWith(label));
      if (prefix) return prefix;
      const norm = (s) => String(s).toLowerCase().replace(/ё/g, 'е').replace(/[^a-zа-я0-9]+/gi, '');
      const nl = norm(label);
      const byNorm = names.find((g) => {
        const ng = norm(g);
        return ng === nl || ng.includes(nl) || nl.includes(ng);
      });
      if (byNorm) return byNorm;
      const words = label.toLowerCase().replace(/ё/g, 'е').split(/[^a-zа-я0-9]+/i).filter((w) => w.length > 3);
      if (words.length >= 2) {
        let best = '';
        let bestScore = 0;
        names.forEach((g) => {
          const gw = g.toLowerCase().replace(/ё/g, 'е').split(/[^a-zа-я0-9]+/i).filter((w) => w.length > 3);
          const score = words.filter((w) => gw.some((x) => x === w || x.startsWith(w.slice(0, 5)) || w.startsWith(x.slice(0, 5)))).length;
          if (score > bestScore) {
            bestScore = score;
            best = g;
          }
        });
        if (bestScore >= 2) return best;
      }
      return direct;
    },
    groupIconKey(groupName) {
      const n = String(groupName || '').toLowerCase();
      if (n.includes('беременност')) return 'pregnancy';
      if (n.includes('послеродов') || n.includes('новорожд')) return 'postpartum';
      if (n.includes('стационар') || n.includes('дневн')) return 'hospital';
      if (n.includes('гинеколог')) return 'gynecology';
      if (n.includes('эко') || (n.includes('репродуктив') && !n.includes('семьи'))) return 'repro';
      if (n.includes('семьи') || n.includes('репродук')) return 'family';
      if (n.includes('консультат')) return 'consult';
      if (n.includes('лучев') || n.includes('функциональн')) return 'scan';
      if (n.includes('патологоанатом') || n.includes('иммуногист')) return 'pathology';
      if (n.includes('лаборатор')) return 'lab';
      if (n.includes('анестез') || n.includes('реанимац')) return 'anesthesia';
      if (n.includes('поддержк') || n.includes('социальн')) return 'support';
      if (n.includes('немедицин')) return 'comfort';
      if (n.includes('обучен')) return 'education';
      if (n.includes('генетик') || n.includes('пренатал')) return 'genetics';
      if (n.includes('род') && !n.includes('беременност') && !n.includes('послеродов')) return 'childbirth';
      return 'default';
    },
    groupIconSrc(groupName) {
      const name = String(groupName || '').trim();
      // Аккордеон: только groupIcons из CMS, иначе хардкод
      const override = name && this.groupIconOverrides[name];
      if (override) return override;
      return GROUP_ICONS[this.groupIconKey(groupName)] || GROUP_ICONS.default;
    },
    hasCmsGroupIcon(groupName) {
      const name = String(groupName || '').trim();
      return !!(name && this.groupIconOverrides[name]);
    },
    isUploadedSrc(src) {
      const s = String(src || '');
      return /^https?:\/\//i.test(s) || s.includes('/api/1/page/image/');
    },
    popularIconSrc(group) {
      const name = group && group.groupName != null ? String(group.groupName).trim() : '';
      // Плитки: иконка плитки → groupIcons → хардкод
      if (name && this.popularIconOverrides[name]) return this.popularIconOverrides[name];
      if (name && this.groupIconOverrides[name]) return this.groupIconOverrides[name];
      return GROUP_ICONS[this.groupIconKey(name)] || GROUP_ICONS.default;
    },
    popularLabel(group) {
      const name = group && group.groupName != null ? String(group.groupName).trim() : '';
      const byName = {};
      byName[name] = true;
      const item = this.configuredPopularItems.find((i) => this.resolvePopularGroupName(i, byName) === name);
      const label = item && item.label != null ? String(item.label).trim() : '';
      return label || name;
    },
    groupAnchorId(groupName) {
      return `paid-group-${encodeURIComponent(String(groupName || ''))}`;
    },
    formatPrice(price) {
      const raw = String(price == null ? '' : price).replace(/\s/g, '').replace(',', '.');
      const num = Number(raw);
      if (!Number.isFinite(num)) {
        const fallback = String(price == null ? '' : price).trim();
        return fallback ? `${fallback} ₽` : '—';
      }
      const formatted = Math.round(num)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return `${formatted} ₽`;
    },
    toggleAccordion(groupName) {
      this.$set(this.openedDepartments, groupName, !this.openedDepartments[groupName]);
    },
    isAccordionOpen(groupName) {
      return !!this.openedDepartments[groupName];
    },
    hasPaidPackage(service) {
      return !!getPaidPackage(service);
    },
    packageKey(serviceId) {
      return String(serviceId == null ? '' : serviceId).trim();
    },
    isPackageOpen(serviceId) {
      const key = this.packageKey(serviceId);
      return !!(key && this.openedPackages[key]);
    },
    togglePackage(serviceId) {
      const key = this.packageKey(serviceId);
      if (!key) return;
      this.$set(this.openedPackages, key, !this.openedPackages[key]);
    },
    toggleAllAccordions() {
      const expand = !this.allExpanded;
      this.paidList.forEach((g) => {
        this.$set(this.openedDepartments, g.groupName, expand);
      });
    },
    openPopularGroup(groupName) {
      if (this.searchState) {
        this.searchState.query = '';
      }
      this.$set(this.openedDepartments, groupName, true);
      const anchor = this.groupAnchorId(groupName);
      // Сброс класса, чтобы анимация подсветки перезапускалась при повторном клике
      this.highlightedGroupAnchor = '';
      if (this.highlightClearTimer) {
        clearTimeout(this.highlightClearTimer);
        this.highlightClearTimer = null;
      }
      this.$nextTick(() => {
        this.highlightedGroupAnchor = anchor;
        this.highlightClearTimer = setTimeout(() => {
          this.highlightedGroupAnchor = '';
          this.highlightedServiceId = null;
          this.highlightClearTimer = null;
        }, 2500);
        // Скроллим к заголовку (кнопке), а не ко всему раскрытому блоку —
        // иначе при длинном списке услуг заголовок уезжает вверх, а не в центр.
        const el = document.getElementById(anchor);
        const header = el && el.querySelector('.paid-accordion__btn');
        const target = header || el;
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },
    toggleAccordionInfo() {
      this.isOpenInfo = !this.isOpenInfo;
    },
    routePaidServices() {
      this.$router.push('/documents/paid-services');
    },
    paidServiceRowId(serviceId) {
      const id = serviceId != null ? String(serviceId) : '';
      return id ? `paid-svc-${encodeURIComponent(id)}` : '';
    },
    isHighlightedService(serviceId) {
      const sid = serviceId != null ? String(serviceId).trim() : '';
      return sid && this.highlightedServiceId === sid;
    },
    normalizeQueryParam(val) {
      if (val == null) {
        return '';
      }
      const s = Array.isArray(val) ? val[0] : val;
      return typeof s === 'string' ? s.trim() : String(s).trim();
    },
    findGroupForService(serviceId, groupHint) {
      const needle = String(serviceId || '').trim();
      if (!needle || !(this.paid || []).length) {
        return null;
      }
      const hint = this.normalizeQueryParam(groupHint);
      if (hint) {
        const byHint = this.paid.find(
          (g) =>
            g
            && String(g.groupName || '') === hint
            && (g.services || []).some((s) => String(s.id || '').trim() === needle)
        );
        if (byHint) {
          return byHint;
        }
      }
      return (
        this.paid.find((g) =>
          (g.services || []).some((s) => String(s.id || '').trim() === needle)
        ) || null
      );
    },
    scrollPaidServiceRowIntoView(rowEl) {
      if (!rowEl || typeof rowEl.scrollIntoView !== 'function') {
        return;
      }
      rowEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    },
    applyServiceFromRoute() {
      const serviceId = this.normalizeQueryParam(this.$route.query.serviceId);
      if (!serviceId) {
        return;
      }
      const group = this.findGroupForService(serviceId, this.$route.query.group);
      if (!group) {
        return;
      }
      this.$set(this.openedDepartments, group.groupName, true);
      if (getPaidPackage({ id: serviceId })) {
        this.$set(this.openedPackages, serviceId, true);
      }
      this.highlightedServiceId = serviceId;
      this.highlightedGroupAnchor = this.groupAnchorId(group.groupName);
      if (this.highlightClearTimer) {
        clearTimeout(this.highlightClearTimer);
        this.highlightClearTimer = null;
      }
      this.highlightClearTimer = setTimeout(() => {
        this.highlightedServiceId = null;
        this.highlightedGroupAnchor = '';
        this.highlightClearTimer = null;
      }, 2500);
      this.$nextTick(() => {
        this.$nextTick(() => {
          const elId = this.paidServiceRowId(serviceId);
          const el = elId ? document.getElementById(elId) : null;
          if (el) {
            this.scrollPaidServiceRowIntoView(el);
          }
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.paid-listing-block {
  width: 100%;
}
</style>
